// application/useVisitas.js  (lista por ciclo + productor/parcela/responsable + eliminar). Sin JSX, sin Swal.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { visitasRepository } from '../infrastructure/visitas.repository';
import { catalogosRepository } from '../../shared/infrastructure/catalogos.repository';
import { productoresConParcela, sortByRecientes, withNombres } from '../domain/visita.rules';

const EMPTY = { items: [], asistentes: [], productores: [], usuarios: [], parcelas: [], status: 'idle', error: null };

// `repository` y `catalogos` son la costura para tests/mocks futuros.
export const useVisitas = ({ repository = visitasRepository, catalogos = catalogosRepository } = {}) => {
  const [state, setState] = useState(EMPTY);
  // null = aún no se conoce el ciclo: lo informa `SelectCiclo` (legacy) al montarse; hasta entonces no se pide nada.
  const [idCiclo, setIdCiclo] = useState(null);
  const mounted = useRef(true);
  const requestId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    if (idCiclo === null || idCiclo === undefined) return;
    const id = ++requestId.current;
    const vigente = () => mounted.current && id === requestId.current;
    setState(prev => ({ ...prev, status: 'loading', error: null }));
    try {
      // El productor de cada visita es su asistente (lista plana de asistentes) -> nombre desde el catálogo de productores.
      const [items, asistentes, productores, usuarios] = await Promise.all([
        repository.list({ idCiclo }),
        catalogos.asistentes(),
        catalogos.productores(idCiclo),
        catalogos.usuarios(),
      ]);
      if (vigente())
        setState({
          items,
          asistentes,
          productores,
          usuarios,
          parcelas: [],
          status: 'success',
          error: null,
        });

      // Segunda etapa (más lenta, ~4 s por productor): parcelas solo de los productores presentes. La tabla ya se ve;
      // mientras llegan (o si falla alguna) se muestra "Parcela {id}".
      const listas = await Promise.all(
        productoresConParcela(items, asistentes).map(idCuenta => catalogos.parcelasDeProductor(idCuenta).catch(() => []))
      );
      if (vigente())
        setState(prev => ({
          ...prev,
          parcelas: listas.reduce((all, lista) => all.concat(lista), []),
        }));
    } catch (error) {
      if (vigente()) setState(prev => (prev.status === 'success' ? prev : { ...EMPTY, status: 'error', error }));
    }
  }, [repository, catalogos, idCiclo]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Estado derivado: nada de guardar una segunda copia de la lista.
  const data = useMemo(
    () =>
      sortByRecientes(
        withNombres(state.items, {
          productores: state.productores,
          parcelas: state.parcelas,
          usuarios: state.usuarios,
          asistentes: state.asistentes,
        })
      ),
    [state.items, state.productores, state.parcelas, state.usuarios, state.asistentes]
  );

  // Lanza el error para que la UI lo muestre (Swal) y decida qué hacer.
  const remove = useCallback(
    async uuid => {
      await repository.remove(uuid);
      await reload();
    },
    [repository, reload]
  );

  return {
    data,
    status: state.status,
    error: state.error,
    idCiclo,
    setIdCiclo,
    reload,
    remove,
  };
};

export default useVisitas;
